// Shared helpers + small hand-drawn SVG chart components for the "Total Performance" views —
// used by both an individual agent's detail page (AgentDetail.js) and the company-wide
// dashboard on the Staff Performance landing page (StaffPerformance.js). Kept as plain inline
// SVG (no charting library) since the app carries none and this is the only place charts are
// needed.

export const TODAY = new Date();

export const daysOverdue = (dateStr) => {
  if (!dateStr) return 0;
  return Math.floor((TODAY - new Date(dateStr)) / 86400000);
};
export const ageBucketOf = (days) => days <= 30 ? '0-30' : days <= 60 ? '31-60' : days <= 90 ? '61-90' : '90+';
export const monthKeyOf = (d) => d.slice(0, 7); // 'YYYY-MM'
export const monthLabelOf = (key) => {
  const [y, m] = key.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};
export const quarterOfMonth = (m) => Math.floor((m - 1) / 3) + 1; // m is 1-12

// Last N month keys ending at the current month, oldest first — e.g. ['2025-10', ..., '2026-09'].
export function lastNMonthKeys(n) {
  const keys = [];
  const d = new Date(TODAY.getFullYear(), TODAY.getMonth(), 1);
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    keys.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
}

export function pctChange(curr, prev) {
  if (!prev) return curr > 0 ? null : 0; // no prior base — can't express as a %, but 0-vs-0 is a real 0
  return ((curr - prev) / prev) * 100;
}

// True aging basis: apply every payment ever received FIFO against debits oldest-first,
// and return the date of the oldest debit that still carries an unpaid remainder — NOT
// simply the earliest debit that ever existed (which stays stuck on an opening balance
// that was cleared months ago). Reversed/voided entries are already filtered out before
// this is called.
export function oldestUnsettledDate(lines) {
  const debits = lines.filter(l => l.debit > 0.005)
    .map(l => ({ date: l.date, remaining: l.debit }))
    .sort((a, b) => a.date.localeCompare(b.date));
  let pool = lines.reduce((s, l) => s + (l.credit || 0), 0);
  for (const d of debits) {
    if (pool <= 0) break;
    const applied = Math.min(pool, d.remaining);
    d.remaining -= applied;
    pool -= applied;
  }
  const firstOpen = debits.find(d => d.remaining > 0.01);
  return firstOpen ? firstOpen.date : null;
}

export const compactNum = (n) => {
  const a = Math.abs(n);
  if (a >= 1e6) return (n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M';
  if (a >= 1e3) return Math.round(n / 1e3) + 'K';
  return String(Math.round(n));
};

// Color a % by how healthy it is — used for collection rate everywhere it appears
// (donut, progress bars, table cells) so the same number always reads the same color.
export function pctColor(pct) {
  if (pct === null || pct === undefined) return 'var(--text-muted)';
  if (pct >= 80) return '#16a34a';
  if (pct >= 50) return '#d97706';
  return '#dc2626';
}

export function ProgressBar({ pct, color, height = 8 }) {
  const clamped = pct === null || pct === undefined ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div style={{ width: '100%', height, background: 'var(--border)', borderRadius: height / 2, overflow: 'hidden' }}>
      <div style={{ width: `${clamped}%`, height: '100%', background: color, borderRadius: height / 2 }} />
    </div>
  );
}

// Ring gauge for a single headline %, e.g. overall collection rate.
export function DonutGauge({ pct, size = 130, stroke = 14, color, label }) {
  const clamped = pct === null || pct === undefined ? 0 : Math.max(0, Math.min(100, pct));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x={size / 2} y={size / 2 + 8} textAnchor="middle" fontSize="24" fontWeight="700" fill={color}>
          {pct === null || pct === undefined ? '—' : `${pct.toFixed(0)}%`}
        </text>
      </svg>
      {label && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</div>}
    </div>
  );
}

// Single horizontal bar split into the four aging buckets, proportional to their share of
// the total outstanding balance — the "at a glance" view of how bad collections are, with
// the numeric breakdown available in the cards underneath.
export function AgingBar({ aging }) {
  const segs = [
    { key: 'b0_30', color: '#16a34a', label: '0–30 Days' },
    { key: 'b31_60', color: '#d97706', label: '31–60 Days' },
    { key: 'b61_90', color: '#ea580c', label: '61–90 Days' },
    { key: 'b90plus', color: '#dc2626', label: '90+ Days' },
  ];
  const total = segs.reduce((s, seg) => s + aging[seg.key], 0);
  if (total <= 0) return null;
  return (
    <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
      {segs.filter(s => aging[s.key] > 0).map(s => (
        <div
          key={s.key}
          title={`${s.label}: ${aging[s.key].toLocaleString(undefined, { maximumFractionDigits: 0 })} (${(aging[s.key] / total * 100).toFixed(0)}%)`}
          style={{ width: `${aging[s.key] / total * 100}%`, background: s.color }}
        />
      ))}
    </div>
  );
}

// Hand-drawn SVG line chart. Plots an arbitrary set of series across the trailing 12
// months on one shared scale — defaults to Sales/Collection (the original two-series use),
// but any {key, color, label} list works, e.g. Tickets/Visas/Packages counts.
export function TrendLineChart({ monthly, series, formatValue = compactNum }) {
  const s = series || [
    { key: 'sales', color: 'var(--primary)', label: 'Sales (invoiced)' },
    { key: 'collection', color: 'var(--success)', label: 'Collection (received)' },
  ];
  const W = 920, H = 260, padL = 58, padR = 18, padT = 18, padB = 40;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = monthly.length;
  const rawMax = Math.max(1, ...monthly.flatMap(m => s.map(ser => m[ser.key] || 0)));
  const pow = Math.pow(10, Math.floor(Math.log10(rawMax)));
  const niceMax = Math.ceil(rawMax / pow) * pow;
  const x = (i) => padL + (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const y = (v) => padT + plotH - (plotH * v) / niceMax;
  const pathOf = (key) => monthly.map((m, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(m[key] || 0).toFixed(1)}`).join(' ');
  const gridSteps = 4;

  return (
    <div style={{ background: 'var(--surface, white)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px 8px', marginBottom: 24 }}>
      <div style={{ display: 'flex', gap: 18, marginBottom: 4, fontSize: 12, color: 'var(--text-muted)', flexWrap: 'wrap' }}>
        {s.map(ser => (
          <span key={ser.key}><span style={{ display: 'inline-block', width: 10, height: 3, background: ser.color, verticalAlign: 'middle', marginRight: 6, borderRadius: 2 }} />{ser.label}</span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {Array.from({ length: gridSteps + 1 }).map((_, gi) => {
          const v = (niceMax * gi) / gridSteps;
          const gy = y(v);
          return (
            <g key={gi}>
              <line x1={padL} x2={W - padR} y1={gy} y2={gy} stroke="var(--border)" strokeWidth="1" />
              <text x={padL - 8} y={gy + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{formatValue(v)}</text>
            </g>
          );
        })}
        {monthly.map((m, i) => (
          <text key={i} x={x(i)} y={H - 22} textAnchor="middle" fontSize="10.5" fill="var(--text-muted)">{m.label}</text>
        ))}
        {s.map(ser => <path key={ser.key} d={pathOf(ser.key)} fill="none" stroke={ser.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />)}
        {s.map(ser => monthly.map((m, i) => <circle key={`${ser.key}${i}`} cx={x(i)} cy={y(m[ser.key] || 0)} r="3" fill={ser.color} />))}
      </svg>
    </div>
  );
}

// Grouped bar chart — Sales vs Collection per quarter, same palette as the trend line chart.
export function QuarterlyBarChart({ quarterly }) {
  const W = 920, H = 220, padL = 54, padR = 18, padT = 18, padB = 36;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const max = Math.max(1, ...quarterly.map(q => Math.max(q.sales, q.collection)));
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const niceMax = Math.ceil(max / pow) * pow;
  const groupW = plotW / quarterly.length;
  const barW = groupW * 0.28;
  const gridSteps = 4;

  return (
    <div style={{ background: 'var(--surface, white)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px 8px' }}>
      <div style={{ display: 'flex', gap: 18, marginBottom: 4, fontSize: 12, color: 'var(--text-muted)' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--primary)', verticalAlign: 'middle', marginRight: 6, borderRadius: 2 }} />Sales</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--success)', verticalAlign: 'middle', marginRight: 6, borderRadius: 2 }} />Collection</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {Array.from({ length: gridSteps + 1 }).map((_, s) => {
          const v = (niceMax * s) / gridSteps;
          const gy = padT + plotH - (plotH * v) / niceMax;
          return (
            <g key={s}>
              <line x1={padL} x2={W - padR} y1={gy} y2={gy} stroke="var(--border)" strokeWidth="1" />
              <text x={padL - 8} y={gy + 4} textAnchor="end" fontSize="11" fill="var(--text-muted)">{compactNum(v)}</text>
            </g>
          );
        })}
        {quarterly.map((q, i) => {
          const gx = padL + i * groupW + groupW / 2;
          const salesH = (plotH * q.sales) / niceMax, collH = (plotH * q.collection) / niceMax;
          const isFuture = q.q > quarterOfMonth(TODAY.getMonth() + 1);
          return (
            <g key={q.q} opacity={isFuture ? 0.35 : 1}>
              <rect x={gx - barW - 2} y={padT + plotH - salesH} width={barW} height={salesH} fill="var(--primary)" rx="2" />
              <rect x={gx + 2} y={padT + plotH - collH} width={barW} height={collH} fill="var(--success)" rx="2" />
              <text x={gx} y={H - 16} textAnchor="middle" fontSize="12" fill="var(--text-muted)" fontWeight="600">Q{q.q}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Horizontal ranked bar chart — e.g. top agents by sales, bar length proportional to the
// max value in the list and colored by an associated health %. When `renderExtra` is given,
// the bar itself is shrunk to make room for it (e.g. that agent's tickets/visas/packages
// counts) between the bar and the value column.
export function RankedBarList({ items, valueKey = 'value', labelKey = 'label', colorKey, maxItems = 10, formatValue = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 }), renderExtra }) {
  const top = items.slice(0, maxItems);
  const max = Math.max(1, ...top.map(i => i[valueKey]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {top.map((item, i) => {
        const pct = (item[valueKey] / max) * 100;
        const color = colorKey ? pctColor(item[colorKey]) : 'var(--primary)';
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div title={item[labelKey]} style={{ width: 150, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item[labelKey]}</div>
            <div style={{ width: renderExtra ? 100 : undefined, flex: renderExtra ? undefined : 1, background: 'var(--border)', borderRadius: 5, height: 18, position: 'relative', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5, transition: 'width .3s' }} />
            </div>
            {renderExtra && <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'flex', flexWrap: 'wrap', gap: 8, minWidth: 0 }}>{renderExtra(item)}</div>}
            <div style={{ width: 90, textAlign: 'right', fontSize: 12.5, fontWeight: 700 }}>{formatValue(item[valueKey])}</div>
          </div>
        );
      })}
      {top.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '10px 0' }}>No data</div>}
    </div>
  );
}
